// PayrollPage v3 — 給与仕訳 処理ページ (2026-04-25)
// KoT → 4 deal 機械生成 + 複式簿記表示 + タイムライン + step-by-step freee ガイド
//
// 4 deal の内訳 (issue=月末同日, due/決済が異なる) — Reader B v3 実証済:
//   1. salary_expense  — 支給発生   issue=月末 / due=翌月10日
//   2. health_insurance — 健保+介護  issue=月末 / due=月末同日 (実引落は翌月初/末で payments[] 消込)
//   3. pension         — 厚年+子拠  issue=月末 / due=月末同日 (同上)
//   4. income_netting  — 控除 netting issue=月末 / due=翌月10日 / wallet 6559057
//
// 住民税納付 は payroll page では生成しない (Deal 4 内 credit に既に含まれる)。
// 翌月10日 西武信金 引落 wallet_txn から銀行取引処理 page で独立 expense deal を起票。
//
// UI 設計:
//   - Control bar: 月選択 / 銀行実額プローブ / プレビュー / 保存 / 承認
//   - KPI row: 6 指標
//   - Timeline: 3 フェーズ (月末 発生 → 翌月1日 社保 → 翌月10日 支給)
//   - 5 Deal cards: 複式簿記テーブル + user actions + expected payments
//   - 過去 run 履歴

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Container,
  PageTitle,
  CardBase,
  HStack,
  VStack,
  Button,
  SelectBox,
  Note,
  Breadcrumbs,
  MessageBlock,
  StatusIcon,
  InlineSpinner,
  MessageDialog,
  MessageDialogConfirm,
} from '@freee_jp/vibes';
import {
  MdPlayArrow,
  MdCheckCircle,
  MdWarning,
  MdError,
  MdGpsFixed,
  MdCalendarToday,
  MdInventory2,
  MdPaid,
  MdDirectionsRun,
  MdMenuBook,
  MdEdit,
  MdCreditCard,
  MdAccountBalance,
  MdBuild,
  MdLink,
  MdRefresh,
  MdAssignmentTurnedIn,
} from 'react-icons/md';
import {
  fetchPayrollPreview,
  fetchPayrollBankProbe,
  buildPayrollRun,
  approvePayrollRun,
  registerPayrollRunToFreee,
  PayrollRegisterResult,
  registerPayrollBankTransfers,
  PayrollBankTransferResult,
  fetchPayrollV4Preview,
  PayrollV4PreviewResult,
  buildPayrollV4,
  registerPayrollV4ToFreee,
  PayrollV4RegisterResult,
  registerPayrollV5ToFreee,
  cleanupPayrollDeals,
  lookupAccountName,
  lookupSectionName,
  lookupTaxName,
  fetchPayrollRuns,
  fetchPayrollStatus,
  PayrollPreviewResponse,
  PayrollDealPreview,
  PayrollDealDetail,
  PayrollRunSummary,
  PayrollBankProbeResponse,
} from '../api/client';

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const S = {
  page: {
    background: 'linear-gradient(180deg, #f8f7f5 0%, #f3f1ef 100%)',
    minHeight: '100vh', paddingBottom: '3rem',
  } as React.CSSProperties,
  kpiRow: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '10px', marginTop: '16px',
  } as React.CSSProperties,
  kpiCard: (accent: string): React.CSSProperties => ({
    background: `linear-gradient(135deg, ${accent}12 0%, ${accent}05 100%)`,
    border: `1px solid ${accent}22`,
    borderRadius: '12px',
    padding: '12px 16px',
  }),
  kpiLabel: { fontSize: '0.6875rem', color: '#6b6560', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase' as const },
  kpiValue: { fontSize: '1.125rem', fontWeight: 700, marginTop: '4px' } as React.CSSProperties,
  kpiSub: { fontSize: '0.75rem', color: '#8a837c', marginTop: '2px' } as React.CSSProperties,

  dealCard: (ok: boolean, phase: string): React.CSSProperties => ({
    background: '#fff',
    border: ok ? '1px solid #c6e7d3' : '1px solid #f0c4c4',
    borderLeft: `5px solid ${ok ? '#2da663' : '#c94444'}`,
    borderRadius: '10px',
    padding: '16px 20px',
    marginBottom: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  }),
  dealHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    gap: '12px', marginBottom: '10px',
  } as React.CSSProperties,
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: '0.8125rem', marginTop: '6px' } as React.CSSProperties,
  th: {
    padding: '7px 10px', fontSize: '0.6875rem', fontWeight: 700, color: '#6b6560',
    textTransform: 'uppercase' as const, letterSpacing: '0.04em',
    borderBottom: '1px solid #e5e4e2', background: '#fafaf8',
    textAlign: 'left' as const, whiteSpace: 'nowrap' as const,
  } as React.CSSProperties,
  td: {
    padding: '6px 10px', fontSize: '0.8125rem',
    borderBottom: '1px solid #f4f3f1', verticalAlign: 'top' as const,
  } as React.CSSProperties,
  amt: { textAlign: 'right' as const, fontVariantNumeric: 'tabular-nums' as const, whiteSpace: 'nowrap' as const },
  sectionCaption: { fontSize: '0.6875rem', color: '#8a837c' },
  boldText: { fontWeight: 700 } as React.CSSProperties,

  timelineWrap: {
    background: '#fff',
    border: '1px solid #e5e4e2',
    borderRadius: '12px',
    padding: '20px 24px',
    marginTop: '16px',
    position: 'relative' as const,
  },
  timelineLine: {
    position: 'absolute' as const, top: '78px', left: '40px', right: '40px',
    height: '3px', background: 'linear-gradient(90deg, #2da663 0%, #48859d 50%, #d68a3c 100%)',
    borderRadius: '2px',
  },
  timelineDot: (bg: string): React.CSSProperties => ({
    position: 'absolute' as const, top: '70px',
    width: '19px', height: '19px', borderRadius: '50%',
    background: bg, border: '3px solid #fff',
    boxShadow: '0 0 0 2px #e5e4e2',
  }),

  actionStep: (done: boolean): React.CSSProperties => ({
    display: 'flex', gap: '10px', padding: '8px 10px',
    background: done ? '#f1f9f4' : '#fbfbf9',
    borderRadius: '6px', marginBottom: '6px',
    border: done ? '1px solid #d0e8d8' : '1px solid #eeece8',
  }),
  actionStepIcon: {
    flexShrink: 0, width: '22px', height: '22px', borderRadius: '50%',
    background: '#2e7cf6', color: '#fff', display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    fontSize: '0.75rem', fontWeight: 700,
  } as React.CSSProperties,
};

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------
const yen = (n: number | null | undefined) =>
  n == null ? '—' : `¥${Number(n).toLocaleString('ja-JP')}`;
const ymd = (s?: string) => (s || '').slice(0, 10);

const currentPaymentYm = () => {
  const d = new Date();
  const prev = new Date(d.getFullYear(), d.getMonth() - 1, 1);
  return { year: prev.getFullYear(), month: prev.getMonth() + 1 };
};

const DEAL_META: Record<string, { label: string; accent: string; phase: string; hint: string }> = {
  salary_expense:   { label: '① 給与発生',        accent: '#2e7cf6', phase: 'accrual',   hint: '支給 総額 (月末発生, 翌月10日 支払)' },
  health_insurance: { label: '② 健康保険料',     accent: '#48859d', phase: 'settlement',hint: '健保+介護 (会社折半 + HQ 残差, due=月末同日 / 実引落は翌月初/末)' },
  pension:          { label: '③ 厚生年金保険料', accent: '#48859d', phase: 'settlement',hint: '厚年+子拠 (会社折半 + HQ 残差, due=月末同日 / 実引落は翌月初/末)' },
  income_netting:   { label: '④ 控除 netting',   accent: '#8a6cd6', phase: 'netting',   hint: '個人預り金集約 (wallet 6559057, 月末 issue / 支給日 due, 住民税預り金 含む)' },
};

// freee 上の正式 walletable_name (省略禁止)
const WALLET_LABELS: Record<number, string> = {
  6559057: '決済口座',
  3838033: '三井住友銀行普通預金',
  3838100: '西武信用金庫現金管理',
};

// ---------------------------------------------------------------------------
// Timeline (SVG)
// ---------------------------------------------------------------------------
const PayrollTimeline: React.FC<{ preview: PayrollPreviewResponse }> = ({ preview }) => {
  const events = preview.clearing_map?.events || [];
  const allDates = Array.from(new Set(events.map(e => e.date))).sort();

  // Group events by date
  const byDate: Record<string, typeof events> = {};
  for (const ev of events) {
    if (!byDate[ev.date]) byDate[ev.date] = [];
    byDate[ev.date].push(ev);
  }

  const segmentColors = ['#2da663', '#48859d', '#d68a3c'];

  return (
    <div style={S.timelineWrap}>
      <div style={{ fontSize: '0.875rem', fontWeight: 700, marginBottom: '16px' }}>
        消込タイムライン ({preview.payment_year}年{preview.payment_month}月分)
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.max(allDates.length, 1)}, 1fr)`, gap: '8px', position: 'relative' }}>
        {allDates.map((date, i) => {
          const evs = byDate[date] || [];
          const color = segmentColors[Math.min(i, 2)];
          return (
            <div key={date} style={{ padding: '0 8px', position: 'relative' }}>
              <div style={{ textAlign: 'center', fontSize: '0.8125rem', fontWeight: 700, color: '#393634' }}>
                {date}
              </div>
              <div style={{ textAlign: 'center', marginTop: '4px', marginBottom: '22px' }}>
                <div style={{
                  display: 'inline-block', width: '18px', height: '18px', borderRadius: '50%',
                  background: color, border: '3px solid #fff', boxShadow: `0 0 0 2px ${color}88`,
                }} />
              </div>
              {evs.map((ev, j) => (
                <div key={j} style={{
                  background: `${color}0f`, border: `1px solid ${color}33`,
                  borderRadius: '8px', padding: '8px 10px', marginBottom: '6px',
                }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#393634' }}>
                    {ev.label}
                  </div>
                  <div style={{ fontSize: '0.6875rem', color: '#6b6560', marginTop: '3px' }}>
                    {ev.description}
                  </div>
                  <div style={{ fontSize: '0.625rem', color: '#8a837c', marginTop: '4px', fontFamily: 'monospace' }}>
                    対象 deal: {ev.deal_seqs.map(n => '①②③④⑤'[n-1] || n).join(' ')}
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Deal Card with 複式簿記 + user actions + expected payments
// ---------------------------------------------------------------------------
const DealCard: React.FC<{ deal: PayrollDealPreview; registered?: boolean }> = ({ deal, registered }) => {
  const meta = DEAL_META[deal.deal_type] || { label: deal.deal_type, accent: '#555', phase: 'other', hint: '' };
  const ok = deal.balance_diff === 0;
  const debitSum = deal.freee_payload.debit_details.reduce((s, d) => s + d.amount, 0);
  const creditSum = deal.freee_payload.credit_details.reduce((s, d) => s + d.amount, 0);

  return (
    <div style={S.dealCard(ok, meta.phase)}>
      <div style={S.dealHeader}>
        <div style={{ flex: '1 1 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <span style={{
              display: 'inline-block', padding: '3px 10px', borderRadius: '6px',
              background: `${meta.accent}22`, color: meta.accent,
              fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.04em',
            }}>
              {deal.freee_payload.type === 'expense' ? 'expense 支出' : 'income 収入'}
            </span>
            <span style={{ ...S.boldText, fontSize: '0.9375rem' }}>{meta.label} — {deal.deal_label}</span>
            {ok ? <StatusIcon type="done">balance OK</StatusIcon> : <StatusIcon type="error">差額 {yen(deal.balance_diff)}</StatusIcon>}
            {registered && <StatusIcon type="done">登録済</StatusIcon>}
            {deal.uses_bank_actual && (
              <span style={{ padding: '2px 8px', background: '#e7f4ec', color: '#2da663', fontSize: '0.6875rem', borderRadius: '4px', fontWeight: 700 }}>
                銀行実額使用
              </span>
            )}
            {deal.uses_bank_actual === false && (meta.phase === 'settlement') && deal.deal_type !== 'residence_tax' && (
              <span style={{ padding: '2px 8px', background: '#fff4e2', color: '#d68a3c', fontSize: '0.6875rem', borderRadius: '4px', fontWeight: 700 }}>
                折半推定 (銀行実額未取得)
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '16px', marginTop: '8px', flexWrap: 'wrap', fontSize: '0.75rem', color: '#6b6560' }}>
            <span>発生日: <b style={{ color: '#393634' }}>{deal.freee_payload.issue_date}</b></span>
            {deal.freee_payload.due_date && (
              <span>期日: <b style={{ color: '#393634' }}>{deal.freee_payload.due_date}</b></span>
            )}
            {deal.freee_payload.from_walletable_id && (
              <span>決済: <b style={{ color: '#393634' }}>{WALLET_LABELS[deal.freee_payload.from_walletable_id] || `wallet ${deal.freee_payload.from_walletable_id}`}</b></span>
            )}
          </div>
          <Note>{meta.hint}</Note>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>{yen(deal.amount)}</div>
          <Note>借 {yen(debitSum)} / 貸 {yen(creditSum)}</Note>
          {deal.hq_residual != null && deal.hq_residual !== 0 && (
            <Note>
              <span style={{ color: '#d68a3c' }}>本部残差 {yen(deal.hq_residual)}</span>
            </Note>
          )}
        </div>
      </div>

      <div style={{ fontSize: '0.8125rem', padding: '6px 10px', background: '#fafaf8', borderRadius: '6px', color: '#555', fontStyle: 'italic' as const }}>
        摘要: {deal.freee_payload.memo}
      </div>

      <DealTable label="借方 (Debit)" rows={deal.freee_payload.debit_details} />
      <DealTable label="貸方 (Credit)" rows={deal.freee_payload.credit_details} />

      {deal.net_breakdown && (
        <div style={{ marginTop: '10px', padding: '10px 12px', background: '#f6f2fa', borderRadius: '6px', border: '1px solid #e5dff1' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#6b4ca6', marginBottom: '4px' }}>控除 netting 内訳</div>
          <Note>
            社保個人 {yen(deal.net_breakdown.shakai_kojin)} / 源泉 {yen(deal.net_breakdown.gensen)} /
            住民税 {yen(deal.net_breakdown.jumin)} / 雇用保険個人 {yen(deal.net_breakdown.koyo_kojin)} /
            制服 {yen(deal.net_breakdown.seifuku)} / 天引 {yen(deal.net_breakdown.tenbiki)} /
            予算報奨金控除 {yen(deal.net_breakdown.hohoukin_kojo)} / 通勤費控除 {yen(deal.net_breakdown.tsukin_kojo)} /
            その他 {yen(deal.net_breakdown.other)}
            {deal.net_breakdown.year_end_refund > 0 && (
              <> / <b style={{ color: '#d68a3c' }}>年末調整還付 {yen(deal.net_breakdown.year_end_refund)}</b></>
            )}
          </Note>
        </div>
      )}

      {/* User actions */}
      {deal.user_actions && deal.user_actions.length > 0 && (
        <div style={{ marginTop: '12px' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#2e7cf6', marginBottom: '6px' }}>
            <span style={{display:'inline-flex',alignItems:'center',gap:6}}><MdBuild /> freee 上でやるべきこと (step-by-step)</span>
          </div>
          {deal.user_actions.map((a, i) => (
            <div key={i} style={S.actionStep(!!registered && a.step <= 1)}>
              <div style={{
                ...S.actionStepIcon,
                background: a.step === 0 ? '#d68a3c' : '#2e7cf6',
              }}>
                {a.step === 0 ? '!' : a.step}
              </div>
              <div style={{ flex: '1 1 auto', fontSize: '0.8125rem' }}>
                <div style={{ fontWeight: 700, color: '#393634' }}>{a.label}</div>
                <div style={{ color: '#6b6560', marginTop: '2px', fontSize: '0.75rem' }}>{a.detail}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Expected payments */}
      {deal.expected_payments && deal.expected_payments.length > 0 && (
        <div style={{ marginTop: '10px', padding: '8px 12px', background: '#f1f6fa', borderRadius: '6px', border: '1px solid #d5e2ec' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#48859d', marginBottom: '4px' }}>
            <span style={{display:'inline-flex',alignItems:'center',gap:6}}><MdLink /> 期待する消込 (wallet_txn との紐付け対象)</span>
          </div>
          {deal.expected_payments.map((p, i) => (
            <div key={i} style={{ fontSize: '0.75rem', color: '#393634', marginBottom: '3px' }}>
              <b>{p.date}</b> · {p.walletable_name} · {p.purpose}
              {p.approximate_amount != null && <> · 予想 {yen(p.approximate_amount)}</>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const DealTable: React.FC<{ label: string; rows: PayrollDealDetail[] }> = ({ label, rows }) => {
  if (!rows.length) return null;
  const total = rows.reduce((s, r) => s + r.amount, 0);
  return (
    <div style={{ marginTop: '8px' }}>
      <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#6b6560', marginBottom: '4px' }}>
        {label} <span style={{ color: '#8a837c', fontWeight: 400 }}>({rows.length}行 / 計 {yen(total)})</span>
      </div>
      <table style={S.table}>
        <thead>
          <tr>
            <th style={S.th}>科目</th>
            <th style={S.th}>部門</th>
            <th style={S.th}>摘要</th>
            <th style={{ ...S.th, ...S.amt }}>金額</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td style={S.td}>
                {r.account_item_name}
                {r.account_item_id && (
                  <div style={S.sectionCaption}>AI {r.account_item_id}{r.tax_code ? ` · tax ${r.tax_code}` : ''}</div>
                )}
                {r.walletable_id && (
                  <div style={S.sectionCaption}>wallet {r.walletable_id}</div>
                )}
              </td>
              <td style={S.td}>
                {r.section_name || '—'}
                {r.section_id && <div style={S.sectionCaption}>sec {r.section_id}</div>}
              </td>
              <td style={S.td}>{r.description}</td>
              <td style={{ ...S.td, ...S.amt, fontWeight: 500 }}>{yen(r.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------
export const PayrollPage: React.FC = () => {
  const initPym = currentPaymentYm();
  const [paymentYear, setPaymentYear] = useState<number>(initPym.year);
  const [paymentMonth, setPaymentMonth] = useState<number>(initPym.month);
  const [preview, setPreview] = useState<PayrollPreviewResponse | null>(null);
  const [bankProbe, setBankProbe] = useState<PayrollBankProbeResponse | null>(null);
  const [kenpoActual, setKenpoActual] = useState<string>('');
  const [pensionActual, setPensionActual] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [probing, setProbing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [registerResult, setRegisterResult] = useState<PayrollRegisterResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [runs, setRuns] = useState<PayrollRunSummary[]>([]);
  const [currentRunId, setCurrentRunId] = useState<number | null>(null);
  const [currentStatus, setCurrentStatus] = useState<string | null>(null);
  const [registeredSeqs, setRegisteredSeqs] = useState<Set<number>>(new Set());

  // 銀行振替 (通勤費 + 給与振込) reconcile state
  const [bankTransferProbing, setBankTransferProbing] = useState(false);
  const [bankTransferRegistering, setBankTransferRegistering] = useState(false);
  const [bankTransferResult, setBankTransferResult] = useState<PayrollBankTransferResult | null>(null);
  const [selectedSalaryTxnIds, setSelectedSalaryTxnIds] = useState<Set<number>>(new Set());

  // v4 spec preview state
  const [v4Loading, setV4Loading] = useState(false);
  const [v4Preview, setV4Preview] = useState<PayrollV4PreviewResult | null>(null);
  const [v4RunId, setV4RunId] = useState<number | null>(null);
  const [v4RunStatus, setV4RunStatus] = useState<string | null>(null);
  const [v4RegisterResult, setV4RegisterResult] = useState<PayrollV4RegisterResult | null>(null);

  // v4 完全自動 button の status: idle → confirming → building → approving → registering → verifying → success / error
  type V4ButtonState = 'idle' | 'confirming' | 'building' | 'approving' | 'registering' | 'verifying' | 'success' | 'error';
  const [v4ButtonState, setV4ButtonState] = useState<V4ButtonState>('idle');
  const [v4ResultModal, setV4ResultModal] = useState<{
    kind: 'success' | 'error';
    title: string;
    message: string;
    details?: string;
    deals?: Array<{ kind: string; status: string; amount?: number; deal_id?: number; scrub?: string; error?: string }>;
  } | null>(null);

  const onV4Preview = useCallback(async () => {
    setV4Loading(true);
    setError(null);
    try {
      const r = await fetchPayrollV4Preview(paymentYear, paymentMonth);
      setV4Preview(r);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setV4Loading(false);
    }
  }, [paymentYear, paymentMonth]);

  // v4 完全自動化フロー: 確認 modal → build → approve → register → 検証 → 結果 modal
  const onV4Autonomous = useCallback(async () => {
    setV4ButtonState('confirming');
  }, []);

  const onV4AutonomousConfirmed = useCallback(async () => {
    setV4ButtonState('building');
    setV4RegisterResult(null);
    setError(null);
    try {
      let runId: number;
      let resolvedStatus: 'draft' | 'approved' | 'registered' | 'failed' = 'draft';
      try {
        const built = await buildPayrollV4(paymentYear, paymentMonth, 'v4-autonomous-flow');
        runId = built.run_id;
        setV4Preview(built);
        resolvedStatus = 'draft';
      } catch (be: any) {
        // 既存 run が approved/registered なら skip build → 既存 run を使う
        const body = be?.body || {};
        if (body.error === 'run_not_in_draft' && body.run_id) {
          runId = Number(body.run_id);
          resolvedStatus = (body.current_status as typeof resolvedStatus) || 'approved';
          // preview は再取得
          try {
            const preview = await fetchPayrollV4Preview(paymentYear, paymentMonth);
            setV4Preview(preview);
          } catch { /* ignore */ }
        } else {
          throw be;
        }
      }
      setV4RunId(runId);
      setV4RunStatus(resolvedStatus);

      const status: string = resolvedStatus;
      if (status === 'registered' || status === 'failed') {
        // 既登録/失敗 → cleanup → re-approve → re-register
        try { await cleanupPayrollDeals(runId); } catch { /* ignore */ }
        await approvePayrollRun(runId, 'v5-autonomous-flow');
        setV4RunStatus('approved');
      } else if (status === 'draft') {
        setV4ButtonState('approving');
        await approvePayrollRun(runId, 'v5-autonomous-flow');
        setV4RunStatus('approved');
      }

      setV4ButtonState('registering');
      const v5Result = await registerPayrollV5ToFreee(runId);
      // v5 結果を v4 結果フォーマットに変換
      const registered: PayrollV4RegisterResult = {
        ok: v5Result.ok,
        spec_version: v5Result.spec_version,
        run_id: v5Result.run_id,
        total_count: v5Result.results.length,
        failed_count: v5Result.results.filter(r => r.status === 'failed').length,
        results: v5Result.results.map(r => ({
          deal_seq: 0,
          deal_type: r.kind,
          status: r.status === 'registered' ? 'registered' : r.status,
          freee_deal_id: r.deal_id,
          amount: r.amount,
          error: r.error,
          scrub_results: r.status === 'registered' && (r as any).scrub ? [{ status: 'reconciled', wallet_txn_id: 0 }] : undefined,
        })),
      };
      setV4RegisterResult(registered);
      setV4RunStatus(registered.ok ? 'registered' : 'failed');

      setV4ButtonState('verifying');
      // 検証: 全 deals registered + 全 wallet_txn reconciled
      const allRegistered = registered.results.every(r => r.status === 'registered' || r.status === 'already_registered');
      const allReconciled = registered.results.every(r =>
        !r.scrub_results || r.scrub_results.every((s: any) => s.status === 'reconciled' || s.status === 'reconciled_split' || s.status === 'already_reconciled' || s.status === 'no_match'),
      );
      const failedScrubs: string[] = [];
      registered.results.forEach((r: any) => {
        (r.scrub_results || []).forEach((s: any) => {
          if (s.status === 'failed') failedScrubs.push(`Deal ${r.deal_seq}: wallet=${s.wallet || s.wallet_txn_id} amt=¥${s.amount?.toLocaleString()} ${s.error || ''}`);
        });
      });

      // v5 result の deals 形式 (= 構造化 list 表示用)
      const dealsList = v5Result.results.map(r => ({
        kind: r.kind,
        status: r.status,
        amount: r.amount,
        deal_id: r.deal_id,
        scrub: (r as any).scrub,
        error: r.error,
      }));

      if (allRegistered && failedScrubs.length === 0) {
        setV4ButtonState('success');
        setV4ResultModal({
          kind: 'success',
          title: '完全自動登録 完了',
          message: `${registered.results.length} 件の取引を登録し、 全 wallet_txn を自動消込しました。`,
          deals: dealsList,
        });
      } else {
        setV4ButtonState('error');
        const errLines = registered.results
          .filter(r => r.status === 'failed')
          .map(r => `Deal ${r.deal_seq} (${r.deal_type}) 登録失敗: ${r.error || '?'}`);
        setV4ResultModal({
          kind: 'error',
          title: '自動登録 部分失敗',
          message: `登録失敗 ${registered.failed_count} 件 / 消込失敗 ${failedScrubs.length} 件`,
          deals: dealsList,
          details: [...errLines, ...failedScrubs].join('\n') || JSON.stringify(registered, null, 2).slice(0, 1500),
        });
      }
    } catch (e: unknown) {
      setV4ButtonState('error');
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      setV4ResultModal({ kind: 'error', title: '自動登録 エラー', message: msg, details: e instanceof Error ? e.stack : undefined });
    }
  }, [paymentYear, paymentMonth]);

  const onV4ResultModalClose = useCallback(() => {
    setV4ResultModal(null);
    setV4ButtonState('idle');
  }, []);

  const monthOptions = useMemo(() => {
    const now = new Date();
    const arr: Array<{ name: string; value: string }> = [];
    for (let i = -12; i <= 1; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const y = d.getFullYear(); const m = d.getMonth() + 1;
      const v = `${y}-${String(m).padStart(2, '0')}`;
      arr.push({ name: `${y}年${m}月 分`, value: v });
    }
    return arr.reverse();
  }, []);

  const currentMonthValue = `${paymentYear}-${String(paymentMonth).padStart(2, '0')}`;

  const loadRuns = useCallback(async () => {
    try {
      const r = await fetchPayrollRuns(20);
      setRuns(r.items);
      const match = r.items.find(x => x.payment_year === paymentYear && x.payment_month === paymentMonth);
      if (match) {
        setCurrentRunId(match.id);
        setCurrentStatus(match.status);
      } else {
        setCurrentRunId(null); setCurrentStatus(null);
      }
    } catch { /* ignore */ }
  }, [paymentYear, paymentMonth]);

  useEffect(() => { loadRuns(); }, [loadRuns]);

  const onProbe = useCallback(async () => {
    setProbing(true);
    setError(null);
    try {
      const p = await fetchPayrollBankProbe(paymentYear, paymentMonth);
      setBankProbe(p);
      if (p.auto_classify.kenpo_candidate) setKenpoActual(String(p.auto_classify.kenpo_candidate.amount));
      if (p.auto_classify.pension_candidate) setPensionActual(String(p.auto_classify.pension_candidate.amount));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setProbing(false);
    }
  }, [paymentYear, paymentMonth]);

  const getActualsForApi = () => ({
    kenpoActual: kenpoActual ? Number(kenpoActual) : undefined,
    pensionActual: pensionActual ? Number(pensionActual) : undefined,
  });

  const onPreview = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetchPayrollPreview(paymentYear, paymentMonth, getActualsForApi());
      setPreview(r);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentYear, paymentMonth, kenpoActual, pensionActual]);

  const onBuild = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const r = await buildPayrollRun(paymentYear, paymentMonth, {
        triggered_by: 'freeeman-ui',
        insurance_actuals: {
          kenpo: kenpoActual ? Number(kenpoActual) : undefined,
          pension: pensionActual ? Number(pensionActual) : undefined,
        },
      });
      setPreview(r);
      if (r.run_id) {
        setCurrentRunId(r.run_id);
        setCurrentStatus('draft');
      }
      await loadRuns();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [paymentYear, paymentMonth, kenpoActual, pensionActual, loadRuns]);

  const onApprove = useCallback(async () => {
    if (!currentRunId) return;
    if (!preview?.balance_zero) {
      alert('balance が 0 でないため承認できません。');
      return;
    }
    if (!window.confirm(`${paymentYear}年${paymentMonth}月分 の 4 仕訳を承認しますか?\n承認後、 「freee 登録 ▸」 button で freee に autonomous 登録できます。`)) return;
    setApproving(true);
    setError(null);
    try {
      await approvePayrollRun(currentRunId, 'user');
      setCurrentStatus('approved');
      await loadRuns();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setApproving(false);
    }
  }, [currentRunId, preview, paymentYear, paymentMonth, loadRuns]);

  const onRegisterToFreee = useCallback(async () => {
    if (!currentRunId) return;
    if (currentStatus !== 'approved' && currentStatus !== 'registered' && currentStatus !== 'failed') {
      alert(`現状 status=${currentStatus} のため freee 登録できません。 先に「一括承認」してください。`);
      return;
    }
    if (!window.confirm(
      `${paymentYear}年${paymentMonth}月分 4 deal を freee に autonomous 登録します。\n` +
      `Deal 1/4: manual_journal (= 振替伝票)、 Deal 2/3: deal + wallet_txn auto-scrub。\n` +
      `freee UI 上の操作は不要です。 続行?`
    )) return;
    setRegistering(true);
    setError(null);
    setRegisterResult(null);
    try {
      const r = await registerPayrollRunToFreee(currentRunId);
      setRegisterResult(r);
      if (r.ok) {
        setCurrentStatus('registered');
        const regs = new Set<number>();
        for (const x of r.results) if (x.freee_deal_id) regs.add(x.deal_seq);
        setRegisteredSeqs(regs);
      }
      await loadRuns();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRegistering(false);
    }
  }, [currentRunId, currentStatus, paymentYear, paymentMonth, loadRuns]);

  // 銀行振替 dry-run (probe + 候補表示)
  const onBankTransferProbe = useCallback(async () => {
    if (!currentRunId) return;
    setBankTransferProbing(true);
    setError(null);
    try {
      const r = await registerPayrollBankTransfers(currentRunId, { dryRun: true });
      setBankTransferResult(r);
      // 自動推奨: best_combination または exact_sum_combination を初期選択
      const auto = r.salary.exact_sum_combination || r.salary.best_combination;
      if (auto) {
        setSelectedSalaryTxnIds(new Set(auto.items.map(it => it.wallet_txn_id)));
      } else {
        setSelectedSalaryTxnIds(new Set());
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBankTransferProbing(false);
    }
  }, [currentRunId]);

  // 銀行振替 確定 (実際の register)
  const onBankTransferRegister = useCallback(async () => {
    if (!currentRunId) return;
    if (!bankTransferResult) {
      alert('先に「銀行振替 dry-run」で候補確認してください');
      return;
    }
    if (!window.confirm(
      `銀行振替を freee に登録します:\n` +
      `- 通勤費 ${bankTransferResult.commute.exact_match_count}件 (西武信金 → 決済口座)\n` +
      `- 給与振込 ${selectedSalaryTxnIds.size}件 (三井住友/西武 → 決済口座)\n` +
      `続行?`
    )) return;
    setBankTransferRegistering(true);
    setError(null);
    try {
      const r = await registerPayrollBankTransfers(currentRunId, {
        salaryWalletTxnIds: Array.from(selectedSalaryTxnIds),
      });
      setBankTransferResult(r);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBankTransferRegistering(false);
    }
  }, [currentRunId, bankTransferResult, selectedSalaryTxnIds]);

  const toggleSalaryTxn = useCallback((txnId: number) => {
    setSelectedSalaryTxnIds(prev => {
      const next = new Set(prev);
      if (next.has(txnId)) next.delete(txnId);
      else next.add(txnId);
      return next;
    });
  }, []);

  const loadExistingRun = useCallback(async (runId: number) => {
    setLoading(true);
    setError(null);
    try {
      const s = await fetchPayrollStatus(runId);
      const deals: PayrollDealPreview[] = s.deals.map(d => ({
        deal_seq: d.deal_seq,
        deal_type: d.deal_type,
        deal_label: DEAL_META[d.deal_type]?.label || d.deal_type,
        amount: d.amount ?? 0,
        balance_diff: 0,
        freee_payload: d.freee_payload,
      }));
      const fakePreview: PayrollPreviewResponse = {
        run_id: runId,
        payment_year: s.run.payment_year,
        payment_month: s.run.payment_month,
        closing_group_ids: [],
        dates: {
          issue_date: '', salary_payment_date: '',
          residence_tax_payment_date: '', shakai_hoken_payment_date: '',
        },
        kot_totals: {
          employee_count: s.run.kot_employee_count ?? 0,
          total_amount: s.run.kot_total_amount ?? 0,
          total_deducation: 0,
          total_transfer: 0,
        },
        deals,
        balance_zero: !!s.run.balance_zero,
        balance_diff: s.run.balance_diff ?? 0,
        balance_detail: [],
        section_breakdown: {},
      };
      setPreview(fakePreview);
      setCurrentRunId(runId);
      setCurrentStatus(s.run.status);
      setPaymentYear(s.run.payment_year);
      setPaymentMonth(s.run.payment_month);
      const regs = new Set<number>();
      for (const d of s.deals) {
        if (d.status === 'registered') regs.add(d.deal_seq);
      }
      setRegisteredSeqs(regs);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const changeMonth = useCallback((v: string) => {
    const [y, m] = v.split('-').map(Number);
    setPaymentYear(y); setPaymentMonth(m);
    setPreview(null); setBankProbe(null);
    setKenpoActual(''); setPensionActual('');
    setRegisteredSeqs(new Set());
    setBankTransferResult(null); setSelectedSalaryTxnIds(new Set());
  }, []);

  return (
    <div style={S.page}>
      <Container>
        <Breadcrumbs links={[{ title: 'FREEEMAN', url: '/' }, { title: '給与仕訳 処理' }]} />
        <PageTitle>給与仕訳 処理</PageTitle>
        <Note>
          ARICA KoT 勤怠支給控除一覧表 → 5 deal 機械生成 (給与発生 / 健保 / 厚年 / 控除 netting / 住民税)。
          0 balance 検証を通過した run のみ承認・登録可。freee への登録は承認後のユーザー判断で手動実行。
        </Note>

        {/* ---------- Controls ---------- */}
        <div style={{ marginTop: '14px' }}>
          <CardBase>
            <div style={{ padding: '16px 20px' }}>
              <HStack gap={1} alignItems="end">
                <div>
                  <Note>対象月 (payment_year/month)</Note>
                  <SelectBox
                    name="payroll-month"
                    value={currentMonthValue}
                    options={monthOptions}
                    onChange={(e) => changeMonth((e.target as HTMLSelectElement).value)}
                  />
                </div>
                {currentRunId && currentStatus && (
                  <Note>run_id={currentRunId} / status: <b>{currentStatus}</b></Note>
                )}
              </HStack>

              {/* Register-to-freee 結果表示 */}
              {registerResult && (
                <div style={{ marginTop: '12px' }}>
                  {registerResult.ok ? (
                    <MessageBlock info>
                      <b style={{display:'inline-flex',alignItems:'center',gap:4}}><MdCheckCircle color="#2e7d32" /> freee 登録完了</b> — {registerResult.total_count} deal、 失敗 {registerResult.failed_count} 件
                      <ul style={{ margin: '6px 0 0 18px', fontSize: '0.75rem' }}>
                        {registerResult.results.map(r => (
                          <li key={r.deal_seq}>
                            Deal {r.deal_seq} ({r.deal_type}): {r.status}
                            {r.freee_deal_id && <> — freee_deal_id={r.freee_deal_id}</>}
                            {r.route && <> [{r.route}]</>}
                            {r.wallet_txn_scrub && (
                              r.wallet_txn_scrub.error
                                ? <span style={{ color: '#a94442' }}> · scrub failed: {r.wallet_txn_scrub.error}</span>
                                : <span style={{ color: '#3c763d' }}> · wallet_txn scrubbed ✓</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </MessageBlock>
                  ) : (
                    <MessageBlock>
                      <b style={{display:'inline-flex',alignItems:'center',gap:4}}><MdWarning color="#ef6c00" /> freee 登録 部分失敗</b> — {registerResult.failed_count} 件 失敗
                      <ul style={{ margin: '6px 0 0 18px', fontSize: '0.75rem' }}>
                        {registerResult.results.map(r => (
                          <li key={r.deal_seq}>
                            Deal {r.deal_seq}: {r.status}
                            {r.error && <span style={{ color: '#a94442' }}> · {r.error}</span>}
                          </li>
                        ))}
                      </ul>
                    </MessageBlock>
                  )}
                </div>
              )}

              {/* 銀行振替 / 旧 bank probe / 旧 actuals 入力欄は v4 完全自動登録 + v4 preview 内 actuals 入力欄に統合済のため削除 */}

              {/* Bank probe note (not yet settled / pair found) */}
              {bankProbe && (
                <div style={{ marginTop: '12px' }}>
                  {bankProbe.not_yet_settled ? (
                    <MessageBlock notice>
                      <b style={{display:'inline-flex',alignItems:'center',gap:4}}><MdCalendarToday /> 社保引落 未到来</b> — {bankProbe.note}
                      <div style={{ fontSize: '0.75rem', marginTop: '4px' }}>
                        {bankProbe.rule} / 検索範囲: {bankProbe.search_range.start} 〜 {bankProbe.search_range.end} ({bankProbe.total_candidates}件 候補)
                      </div>
                      <div style={{ fontSize: '0.75rem', marginTop: '2px' }}>
                        → 引落後に再 probe するか、納入告知書から手動で下に実額入力してください。手動入力なしでも折半推定で承認・登録可能 (本部差額吸収 line は省略)。
                      </div>
                    </MessageBlock>
                  ) : (
                    <MessageBlock info>
                      <b style={{display:'inline-flex',alignItems:'center',gap:4}}><MdAccountBalance /> 銀行 probe 結果</b> — {bankProbe.note}
                      <div style={{ fontSize: '0.75rem', marginTop: '4px' }}>
                        {bankProbe.rule || ''} · 検索範囲: {bankProbe.search_range.start} 〜 {bankProbe.search_range.end} ({bankProbe.total_candidates}件 候補)
                        {bankProbe.pair_dates_found && bankProbe.pair_dates_found.length > 0 && (
                          <> · 同日ペア検出: {bankProbe.pair_dates_found.join(', ')}</>
                        )}
                      </div>
                    </MessageBlock>
                  )}
                </div>
              )}

              {/* Manual override / actual input — 旧 v3 入力欄、 v4 preview 内に統合済のため非表示 */}
              <div style={{ display: 'none', marginTop: '12px', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <Note>健保+介護 納入告知書実額 (optional)</Note>
                  <input
                    type="number"
                    value={kenpoActual}
                    onChange={(e) => setKenpoActual(e.target.value)}
                    placeholder="例: 1494650"
                    style={{ width: '100%', padding: '8px', border: '1px solid #cfcdc8', borderRadius: '4px', fontSize: '0.875rem' }}
                  />
                  {bankProbe?.auto_classify.kenpo_candidate && (
                    <Note>
                      候補: {bankProbe.auto_classify.kenpo_candidate.date} · {yen(bankProbe.auto_classify.kenpo_candidate.amount)} · {bankProbe.auto_classify.kenpo_candidate.description}
                    </Note>
                  )}
                </div>
                <div>
                  <Note>厚年+子拠 納入告知書実額 (optional)</Note>
                  <input
                    type="number"
                    value={pensionActual}
                    onChange={(e) => setPensionActual(e.target.value)}
                    placeholder="例: 2435130"
                    style={{ width: '100%', padding: '8px', border: '1px solid #cfcdc8', borderRadius: '4px', fontSize: '0.875rem' }}
                  />
                  {bankProbe?.auto_classify.pension_candidate && (
                    <Note>
                      候補: {bankProbe.auto_classify.pension_candidate.date} · {yen(bankProbe.auto_classify.pension_candidate.amount)} · {bankProbe.auto_classify.pension_candidate.description}
                    </Note>
                  )}
                </div>
              </div>

              {currentRunId && (
                <Note>
                  run_id: <b>{currentRunId}</b> / status: <b>{currentStatus}</b>
                  {registeredSeqs.size > 0 && <> / 登録済: <b>{registeredSeqs.size}/4</b></>}
                </Note>
              )}
            </div>
          </CardBase>
        </div>

        {error && (
          <div style={{ marginTop: '14px' }}>
            <MessageBlock error>{error}</MessageBlock>
          </div>
        )}

        {/* ---------- v4 spec preview (csv_row_json ベース 完璧 0 検証) ---------- */}
        <div style={{ marginTop: '14px' }}>
          <CardBase>
            <div style={{ padding: '16px 20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <div style={{ fontSize: '0.9rem', fontWeight: 700, display:'inline-flex', alignItems:'center', gap:6 }}><MdGpsFixed /> 仕訳プレビュー (完璧 0 検証 + 立替金 + 自動消込)</div>
                <HStack gap={1}>
                  <Button onClick={onV4Preview} disabled={v4Loading || v4ButtonState !== 'idle'}>
                    {v4Loading ? <InlineSpinner isLoading /> : null} プレビュー取得
                  </Button>
                  {(() => {
                    const stateLabels: Record<typeof v4ButtonState, { label: React.ReactNode; bgColor?: string; disabled: boolean }> = {
                      idle:        { label: <><MdPlayArrow /> 完全自動登録 (店舗別仕訳 + 全 wallet_txn 紐付け)</>, disabled: false },
                      confirming:  { label: <><InlineSpinner isLoading /> 確認中…</>, bgColor: '#9e9e9e', disabled: true },
                      building:    { label: <><InlineSpinner isLoading /> ① D1 保存中…</>, bgColor: '#9e9e9e', disabled: true },
                      approving:   { label: <><InlineSpinner isLoading /> ② 承認中…</>, bgColor: '#9e9e9e', disabled: true },
                      registering: { label: <><InlineSpinner isLoading /> ③ freee 登録 + 消込中…</>, bgColor: '#9e9e9e', disabled: true },
                      verifying:   { label: <><InlineSpinner isLoading /> ④ 検証中…</>, bgColor: '#ffa726', disabled: true },
                      success:     { label: <><MdCheckCircle /> 完了 (再実行は cleanup 後)</>, bgColor: '#4caf50', disabled: true },
                      error:       { label: <><MdWarning /> エラー (詳細はモーダル参照)</>, bgColor: '#f44336', disabled: true },
                    };
                    const s = stateLabels[v4ButtonState];
                    if (!s.bgColor) {
                      return <Button primary={v4ButtonState === 'idle'} onClick={onV4Autonomous} disabled={s.disabled}>{s.label}</Button>;
                    }
                    return (
                      <button
                        onClick={onV4Autonomous}
                        disabled={s.disabled}
                        style={{
                          padding: '8px 16px',
                          background: s.bgColor,
                          color: '#fff',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '0.875rem',
                          fontWeight: 700,
                          cursor: s.disabled ? 'not-allowed' : 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                      >
                        {s.label}
                      </button>
                    );
                  })()}
                </HStack>
              </div>
              <Note>csv_row_json ベース、 振込/現金 完全判別、 退職者住民税立替自動検出、 freee 自動消込対応 (= payments[] で wallet_txn 自動 link)</Note>

              {/* 健保+厚年 実額 (= preview 内部で auto-probe 済、 手動 override 用) */}
              <div style={{ marginTop: '12px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <Note>健保+介護 納入告知書 実額 (= 月末三井住友 引落、 自動取得済)</Note>
                  <input
                    type="number"
                    value={kenpoActual}
                    onChange={(e) => setKenpoActual(e.target.value)}
                    placeholder="例: 1483930"
                    style={{ width: '100%', padding: '8px', border: '1px solid #cfcdc8', borderRadius: '4px', fontSize: '0.875rem' }}
                  />
                  {v4Preview?.actuals?.kenpo ? <Note>自動取得値: ¥{v4Preview.actuals.kenpo.toLocaleString()}</Note> : null}
                </div>
                <div>
                  <Note>厚年+子拠 納入告知書 実額 (= 月末三井住友 引落、 自動取得済)</Note>
                  <input
                    type="number"
                    value={pensionActual}
                    onChange={(e) => setPensionActual(e.target.value)}
                    placeholder="例: 2593740"
                    style={{ width: '100%', padding: '8px', border: '1px solid #cfcdc8', borderRadius: '4px', fontSize: '0.875rem' }}
                  />
                  {v4Preview?.actuals?.pension ? <Note>自動取得値: ¥{v4Preview.actuals.pension.toLocaleString()}</Note> : null}
                </div>
              </div>

              {v4Preview && (
                <div style={{ marginTop: '12px' }}>
                  {/* 検証結果 */}
                  <div style={{ padding: '10px', background: v4Preview.verification.perfect_zero ? '#e8f5e9' : '#fff3cd', border: '1px solid', borderColor: v4Preview.verification.perfect_zero ? '#4caf50' : '#ffc107', borderRadius: '4px', marginBottom: '12px' }}>
                    <div style={{ fontWeight: 700, fontSize: '0.85rem' }}>
                      {v4Preview.verification.perfect_zero
                        ? <span style={{display:'inline-flex',alignItems:'center',gap:4}}><MdCheckCircle color="#2e7d32" /> 完璧 0 達成</span>
                        : <span style={{display:'inline-flex',alignItems:'center',gap:4}}><MdWarning color="#ef6c00" /> balance_diff != 0</span>}
                    </div>
                    <div style={{ fontSize: '0.75rem', marginTop: '4px', fontFamily: 'monospace' }}>
                      Deal 1 amount: {yen(v4Preview.deal1.amount)} = payments_sum {yen(v4Preview.verification.payments_sum)} (差 {yen(v4Preview.verification.balance_diff)})
                    </div>
                  </div>

                  {/* payments[6件] テーブル */}
                  <div style={{ fontSize: '0.85rem', fontWeight: 700, margin: '8px 0 4px', display:'inline-flex', alignItems:'center', gap:6 }}><MdInventory2 /> Deal 1 payments[6件] (= 自動消込対象 wallet_txn)</div>
                  <table style={{ width: '100%', fontSize: '0.75rem', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #ccc', background: '#f5f5f5' }}>
                        <th style={{ textAlign: 'left', padding: '4px' }}>#</th>
                        <th style={{ textAlign: 'left', padding: '4px' }}>口座/wallet</th>
                        <th style={{ textAlign: 'left', padding: '4px' }}>日付</th>
                        <th style={{ textAlign: 'right', padding: '4px' }}>金額</th>
                        <th style={{ textAlign: 'left', padding: '4px' }}>摘要</th>
                      </tr>
                    </thead>
                    <tbody>
                      {v4Preview.deal1.freee_payload.payments.map((p, i) => {
                        let label = '?';
                        if (p.walletable_type === 'bank_account' && p.walletable_id === 3838033) label = '三井住友銀行普通預金';
                        else if (p.walletable_type === 'bank_account' && p.walletable_id === 3838100) label = '西武信用金庫現金管理';
                        else if (p.walletable_type === 'wallet' && p.walletable_id === 6559057) label = '決済口座';
                        return (
                          <tr key={i} style={{ borderBottom: '1px solid #f5f5f5' }}>
                            <td style={{ padding: '4px' }}>{i + 1}</td>
                            <td style={{ padding: '4px' }}>{label}</td>
                            <td style={{ padding: '4px' }}>{p.date}</td>
                            <td style={{ padding: '4px', textAlign: 'right', fontFamily: 'monospace' }}>{yen(p.amount)}</td>
                            <td style={{ padding: '4px' }}>{p.description}</td>
                          </tr>
                        );
                      })}
                      <tr style={{ borderTop: '1px solid #ccc', fontWeight: 'bold', background: '#fafafa' }}>
                        <td colSpan={3} style={{ padding: '4px', textAlign: 'right' }}>payments合計:</td>
                        <td style={{ padding: '4px', textAlign: 'right', fontFamily: 'monospace' }}>{yen(v4Preview.verification.payments_sum)}</td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>

                  {/* 立替金検出 */}
                  {v4Preview.tatekae_negative.length > 0 && (
                    <div style={{ marginTop: '12px' }}>
                      <div style={{ fontSize: '0.85rem', fontWeight: 700, display:'inline-flex', alignItems:'center', gap:6 }}><MdPaid /> 立替金検出 (net マイナス社員)</div>
                      <ul style={{ margin: '4px 0 0 18px', fontSize: '0.75rem' }}>
                        {v4Preview.tatekae_negative.map(t => (
                          <li key={t.code}>
                            {t.name} ({t.code}) ¥{t.amount.toLocaleString()} — {t.desc}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* 退職者住民税一括 */}
                  {v4Preview.retired_with_jumin.length > 0 && (
                    <div style={{ marginTop: '12px' }}>
                      <div style={{ fontSize: '0.85rem', fontWeight: 700, display:'inline-flex', alignItems:'center', gap:6 }}><MdDirectionsRun /> 退職者 住民税 一括代行納付 (4-5 月分)</div>
                      <ul style={{ margin: '4px 0 0 18px', fontSize: '0.75rem' }}>
                        {v4Preview.retired_with_jumin.map(r => (
                          <li key={r.code}>
                            {r.name} ({r.code}) 退職 {r.resigned_date} / 月¥{r.monthly_jumin.toLocaleString()} × 2ヶ月 = ¥{r.bulk_amount.toLocaleString()}
                          </li>
                        ))}
                        <li style={{ fontWeight: 'bold', marginTop: '4px' }}>
                          一括計: ¥{v4Preview.retired_with_jumin.reduce((s, r) => s + r.bulk_amount, 0).toLocaleString()} (= 住民税納付 deal の借方 立替金 line として計上)
                        </li>
                      </ul>
                    </div>
                  )}

                  {/* KoT 集計 */}
                  <div style={{ marginTop: '12px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', fontSize: '0.75rem' }}>
                    <div style={{ padding: '6px', background: '#f5f5f5', borderRadius: '4px' }}>
                      <div style={{ fontWeight: 700 }}>総支給</div>
                      <div style={{ fontFamily: 'monospace' }}>{yen(v4Preview.kot_totals.total_payment)}</div>
                    </div>
                    <div style={{ padding: '6px', background: '#f5f5f5', borderRadius: '4px' }}>
                      <div style={{ fontWeight: 700 }}>控除合計</div>
                      <div style={{ fontFamily: 'monospace' }}>{yen(v4Preview.kot_totals.total_deduction)}</div>
                    </div>
                    <div style={{ padding: '6px', background: '#f5f5f5', borderRadius: '4px' }}>
                      <div style={{ fontWeight: 700 }}>差引振込</div>
                      <div style={{ fontFamily: 'monospace' }}>{yen(v4Preview.kot_totals.net_payment)}</div>
                    </div>
                  </div>

                  {/* v4 register 結果 */}
                  {v4RegisterResult && (
                    <div style={{ marginTop: '12px', padding: '10px', background: v4RegisterResult.ok ? '#e8f5e9' : '#ffebee', border: '1px solid', borderColor: v4RegisterResult.ok ? '#4caf50' : '#f44336', borderRadius: '4px' }}>
                      <div style={{ fontWeight: 700, fontSize: '0.85rem' }}>
                        {v4RegisterResult.ok
                          ? <span style={{display:'inline-flex',alignItems:'center',gap:4}}><MdCheckCircle color="#2e7d32" /> freee 登録 完了</span>
                          : <span style={{display:'inline-flex',alignItems:'center',gap:4}}><MdWarning color="#ef6c00" /> freee 登録 部分失敗</span>} (run_id={v4RegisterResult.run_id})
                      </div>
                      <ul style={{ margin: '6px 0 0 18px', fontSize: '0.75rem' }}>
                        {v4RegisterResult.results.map(r => (
                          <li key={r.deal_seq}>
                            Deal {r.deal_seq} ({r.deal_type}) — <b>{r.status}</b>
                            {r.freee_deal_id && <> · freee_deal_id={r.freee_deal_id}</>}
                            {r.payments_count != null && <> · payments={r.payments_count}件 自動消込</>}
                            {r.error && <span style={{ color: '#a94442' }}> · {r.error}</span>}
                          </li>
                        ))}
                      </ul>
                      {v4RegisterResult.bank_transfers && (
                        <div style={{ marginTop: '6px', fontSize: '0.7rem', color: '#666' }}>
                          銀行振替 (通勤費 西武→決済口座): {JSON.stringify(v4RegisterResult.bank_transfers).slice(0, 200)}
                        </div>
                      )}
                    </div>
                  )}

                  {/* ---------- 仕訳プレビュー (= freee 仕訳帳スタイル) ---------- */}
                  <div style={{ marginTop: '16px', borderTop: '2px solid #2e7cf6', paddingTop: '12px' }}>
                    <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: '0 0 8px', color: '#393634' }}>
                      <span style={{display:'inline-flex',alignItems:'center',gap:6}}><MdMenuBook /> 仕訳プレビュー — {paymentYear}年{paymentMonth}月分 給与処理</span>
                    </h3>
                    <Note>各 deal の借方/貸方を freee 仕訳帳と同じ形式で表示。 登録時はこの内容がそのまま freee に反映されます。</Note>

                    {[
                      { seq: 1, color: '#2e7cf6', label: '給与発生', type: 'expense' as const, deal: v4Preview.deal1, issue_date: v4Preview.deal1.freee_payload.issue_date },
                      { seq: 2, color: '#48859d', label: '健保+介護 引落', type: 'expense' as const, deal: (v4Preview as any).deal2, issue_date: (v4Preview as any).deal2?.freee_payload?.issue_date },
                      { seq: 3, color: '#48859d', label: '厚年+子拠 引落', type: 'expense' as const, deal: (v4Preview as any).deal3, issue_date: (v4Preview as any).deal3?.freee_payload?.issue_date },
                      { seq: 4, color: '#7e57c2', label: '控除内訳', type: 'income' as const, deal: (v4Preview as any).deal4, issue_date: (v4Preview as any).deal4?.freee_payload?.issue_date },
                      { seq: 5, color: '#ef6c00', label: '住民税納付', type: 'expense' as const, deal: v4Preview.deal_jumin, issue_date: v4Preview.deal_jumin?.freee_payload?.issue_date },
                    ].filter(d => d.deal && d.deal.amount > 0).map(({ seq, color, label, type, deal, issue_date }) => {
                      const details: any[] = deal.freee_payload?.details || [];
                      const debits = details.filter((d: any) => d.entry_side === 'debit');
                      const credits = details.filter((d: any) => d.entry_side === 'credit');
                      const payments = deal.freee_payload?.payments || [];
                      const debitSum = debits.reduce((s: number, d: any) => s + d.amount, 0);
                      const creditSum = credits.reduce((s: number, d: any) => s + d.amount, 0);
                      return (
                        <div key={seq} style={{ marginTop: '10px' }}><CardBase>
                          <div style={{ padding: '12px 16px' }}>
                            {/* deal header */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderLeft: `4px solid ${color}`, paddingLeft: '10px', marginBottom: '10px' }}>
                              <div>
                                <span style={{ fontSize: '0.75rem', color: '#666', marginRight: '8px' }}>Deal {seq}</span>
                                <span style={{ fontWeight: 700, fontSize: '1rem' }}>{label}</span>
                                <span style={{ fontSize: '0.75rem', color: '#666', marginLeft: '8px', padding: '2px 6px', background: type === 'income' ? '#e3f2fd' : '#fff3e0', borderRadius: '3px' }}>{type}</span>
                              </div>
                              <div style={{ textAlign: 'right' }}>
                                <div style={{ fontSize: '1.1rem', fontWeight: 700, fontFamily: 'monospace', color }}>¥{deal.amount.toLocaleString()}</div>
                                <div style={{ fontSize: '0.7rem', color: '#666' }}>発生日: {issue_date || '-'}</div>
                              </div>
                            </div>

                            {/* 仕訳テーブル (= freee 仕訳帳形式: 借方 | 貸方) */}
                            <table style={{ width: '100%', fontSize: '0.8rem', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                              <thead>
                                <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                                  <th colSpan={4} style={{ padding: '6px', textAlign: 'left', borderRight: '1px solid #ddd', fontWeight: 700, color: '#48859d' }}>借方</th>
                                  <th colSpan={4} style={{ padding: '6px', textAlign: 'left', fontWeight: 700, color: '#ef6c00' }}>貸方</th>
                                </tr>
                                <tr style={{ background: '#fafafa', borderBottom: '1px solid #ddd', fontSize: '0.7rem' }}>
                                  <th style={{ padding: '4px 6px', textAlign: 'left', width: '18%' }}>勘定科目</th>
                                  <th style={{ padding: '4px 6px', textAlign: 'left', width: '14%' }}>部門</th>
                                  <th style={{ padding: '4px 6px', textAlign: 'right', width: '10%' }}>金額</th>
                                  <th style={{ padding: '4px 6px', textAlign: 'left', width: '8%', borderRight: '1px solid #ddd' }}>税区分</th>
                                  <th style={{ padding: '4px 6px', textAlign: 'left', width: '18%' }}>勘定科目</th>
                                  <th style={{ padding: '4px 6px', textAlign: 'left', width: '14%' }}>部門</th>
                                  <th style={{ padding: '4px 6px', textAlign: 'right', width: '10%' }}>金額</th>
                                  <th style={{ padding: '4px 6px', textAlign: 'left', width: '8%' }}>税区分</th>
                                </tr>
                              </thead>
                              <tbody>
                                {Array.from({ length: Math.max(debits.length, credits.length, 1) }, (_, i) => {
                                  const dl: any = debits[i]; const cl: any = credits[i];
                                  return (
                                    <React.Fragment key={i}>
                                      <tr style={{ borderBottom: '1px dashed #eee' }}>
                                        <td style={{ padding: '5px 6px', verticalAlign: 'top' }}>{dl ? lookupAccountName(dl.account_item_id) : ''}</td>
                                        <td style={{ padding: '5px 6px', verticalAlign: 'top', color: '#666', fontSize: '0.72rem' }}>{dl ? lookupSectionName(dl.section_id) : ''}</td>
                                        <td style={{ padding: '5px 6px', verticalAlign: 'top', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>{dl ? `¥${dl.amount.toLocaleString()}` : ''}</td>
                                        <td style={{ padding: '5px 6px', verticalAlign: 'top', borderRight: '1px solid #ddd', fontSize: '0.7rem', color: '#888' }}>{dl ? lookupTaxName(dl.tax_code) : ''}</td>
                                        <td style={{ padding: '5px 6px', verticalAlign: 'top' }}>{cl ? lookupAccountName(cl.account_item_id) : ''}</td>
                                        <td style={{ padding: '5px 6px', verticalAlign: 'top', color: '#666', fontSize: '0.72rem' }}>{cl ? lookupSectionName(cl.section_id) : ''}</td>
                                        <td style={{ padding: '5px 6px', verticalAlign: 'top', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>{cl ? `¥${cl.amount.toLocaleString()}` : ''}</td>
                                        <td style={{ padding: '5px 6px', verticalAlign: 'top', fontSize: '0.7rem', color: '#888' }}>{cl ? lookupTaxName(cl.tax_code) : ''}</td>
                                      </tr>
                                      <tr style={{ borderBottom: '1px solid #f5f5f5', background: '#fcfcfc' }}>
                                        <td colSpan={4} style={{ padding: '2px 6px 6px', fontSize: '0.68rem', color: '#777', borderRight: '1px solid #ddd' }}>
                                          {dl?.description ? <span style={{display:'inline-flex',alignItems:'center',gap:4}}><MdEdit size={11} /> {dl.description}</span> : null}
                                        </td>
                                        <td colSpan={4} style={{ padding: '2px 6px 6px', fontSize: '0.68rem', color: '#777' }}>
                                          {cl?.description ? <span style={{display:'inline-flex',alignItems:'center',gap:4}}><MdEdit size={11} /> {cl.description}</span> : null}
                                        </td>
                                      </tr>
                                    </React.Fragment>
                                  );
                                })}
                              </tbody>
                              <tfoot>
                                <tr style={{ background: '#f0f0f0', fontWeight: 700, borderTop: '2px solid #999' }}>
                                  <td colSpan={2} style={{ padding: '6px', textAlign: 'right' }}>借方合計</td>
                                  <td style={{ padding: '6px', textAlign: 'right', fontFamily: 'monospace', borderRight: '1px solid #ddd' }} colSpan={2}>¥{debitSum.toLocaleString()}</td>
                                  <td colSpan={2} style={{ padding: '6px', textAlign: 'right' }}>貸方合計</td>
                                  <td style={{ padding: '6px', textAlign: 'right', fontFamily: 'monospace' }} colSpan={2}>¥{creditSum.toLocaleString()}</td>
                                </tr>
                              </tfoot>
                            </table>

                            {/* payments[] */}
                            {payments.length > 0 && (
                              <div style={{ marginTop: '10px', padding: '8px', background: '#f8f9fa', borderRadius: '4px' }}>
                                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#48859d', marginBottom: '4px' }}>
                                  <span style={{display:'inline-flex',alignItems:'center',gap:4}}><MdCreditCard /> 自動消込 wallet_txn ({payments.length} 件)</span>
                                </div>
                                <table style={{ width: '100%', fontSize: '0.72rem', borderCollapse: 'collapse' }}>
                                  <thead>
                                    <tr style={{ background: '#fff' }}>
                                      <th style={{ padding: '3px 6px', textAlign: 'left' }}>日付</th>
                                      <th style={{ padding: '3px 6px', textAlign: 'left' }}>口座</th>
                                      <th style={{ padding: '3px 6px', textAlign: 'right' }}>金額</th>
                                      <th style={{ padding: '3px 6px', textAlign: 'left' }}>摘要</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {payments.map((p: any, i: number) => {
                                      let walletLabel = '?';
                                      let walletColor = '#666';
                                      if (p.walletable_type === 'bank_account' && p.walletable_id === 3838033) { walletLabel = '三井住友普通預金'; walletColor = '#1976d2'; }
                                      else if (p.walletable_type === 'bank_account' && p.walletable_id === 3838100) { walletLabel = '西武信金'; walletColor = '#d32f2f'; }
                                      else if (p.walletable_type === 'wallet' && p.walletable_id === 6559057) { walletLabel = '決済口座'; walletColor = '#7e57c2'; }
                                      return (
                                        <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                                          <td style={{ padding: '3px 6px', fontFamily: 'monospace' }}>{p.date}</td>
                                          <td style={{ padding: '3px 6px', color: walletColor, fontWeight: 600 }}>{walletLabel}</td>
                                          <td style={{ padding: '3px 6px', textAlign: 'right', fontFamily: 'monospace' }}>¥{p.amount.toLocaleString()}</td>
                                          <td style={{ padding: '3px 6px', color: '#888', fontSize: '0.7rem' }}>{p.description || '-'}</td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        </CardBase></div>
                      );
                    })}
                  </div>

                  {/* 自動消込フロー説明 */}
                  <div style={{ marginTop: '12px', padding: '8px', background: '#fffbe6', border: '1px solid #ffe082', borderRadius: '4px', fontSize: '0.7rem' }}>
                    <div style={{ fontWeight: 700, marginBottom: '4px', display:'inline-flex', alignItems:'center', gap:6 }}><MdRefresh /> 自動消込フロー</div>
                    <ol style={{ margin: '0 0 0 18px' }}>
                      <li>worker → freee API <code>POST /api/1/deals</code> で Deal 1 (expense) を payments[6件] と一緒に送信</li>
                      <li>freee が「同日同額の wallet_txn」 を payments の walletable から探して自動 link → wallet_txn status=2 (settled)</li>
                      <li>三井住友 4/8 ¥14,583,540 / ¥546,521、 西武 4/9 ¥2,171,779、 西武 2/25 ¥53,270、 西武 4/06 ¥30,000 が同時に消込される</li>
                      <li>決済口座 ¥2,810,081 は Deal 4 (income) と相殺で残高 0 (= Deal 4 も同 amount で逆方向)</li>
                      <li>住民税納付 ¥410,600 (西武 4/9) は別 deal (= 退職者立替 ¥74,000 含む) で消込</li>
                    </ol>
                    <div style={{ marginTop: '4px', color: '#cc6600' }}>※ v4 register endpoint は実装中。 完成後にこのフロー全体が自動実行されます</div>
                  </div>
                </div>
              )}
            </div>
          </CardBase>
        </div>

        {/* ---------- Preview ---------- */}
        {preview && (
          <>
            <div style={S.kpiRow}>
              <div style={S.kpiCard('#2e7cf6')}>
                <div style={S.kpiLabel}>対象月</div>
                <div style={S.kpiValue}>{preview.payment_year}年{preview.payment_month}月分</div>
                <div style={S.kpiSub}>closing: {preview.closing_group_ids.join(', ') || '—'}</div>
              </div>
              <div style={S.kpiCard('#48859d')}>
                <div style={S.kpiLabel}>対象社員</div>
                <div style={S.kpiValue}>{preview.kot_totals.employee_count}名</div>
              </div>
              <div style={S.kpiCard('#2da663')}>
                <div style={S.kpiLabel}>総支給 (KoT)</div>
                <div style={S.kpiValue}>{yen(preview.kot_totals.total_amount)}</div>
              </div>
              <div style={S.kpiCard('#8a6cd6')}>
                <div style={S.kpiLabel}>控除計 (KoT)</div>
                <div style={S.kpiValue}>{yen(preview.kot_totals.total_deducation)}</div>
              </div>
              <div style={S.kpiCard('#d68a3c')}>
                <div style={S.kpiLabel}>差引振込 (KoT)</div>
                <div style={S.kpiValue}>{yen(preview.kot_totals.total_transfer)}</div>
              </div>
              <div style={S.kpiCard(preview.balance_zero ? '#2da663' : '#c94444')}>
                <div style={S.kpiLabel}>balance 検証</div>
                <div style={S.kpiValue}>
                  {preview.balance_zero ? '✓ 0 (OK)' : `✗ ${yen(preview.balance_diff)}`}
                </div>
                {preview.kot_totals.year_end_adjustment_refund ? (
                  <div style={S.kpiSub}>
                    年調還付: {yen(preview.kot_totals.year_end_adjustment_refund)}
                  </div>
                ) : null}
              </div>
            </div>

            {!preview.balance_zero && preview.balance_detail.length > 0 && (
              <div style={{ marginTop: '12px' }}>
                <MessageBlock error>
                  <div>balance 検証失敗:</div>
                  <pre style={{ fontSize: '0.7rem', marginTop: '6px', whiteSpace: 'pre-wrap' }}>
                    {JSON.stringify(preview.balance_detail, null, 2)}
                  </pre>
                </MessageBlock>
              </div>
            )}

            {/* Timeline */}
            {preview.clearing_map && <PayrollTimeline preview={preview} />}

            {/* Deal cards (= v3 builder の旧 spec preview。 v4Preview がある場合は重複表示を回避) */}
            {!v4Preview && (
              <div style={{ marginTop: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontSize: '0.9375rem', fontWeight: 700 }}>
                    仕訳プレビュー ({preview.deals.length}件)
                  </span>
                  <Note>複式簿記形式 · freee 登録前の最終確認</Note>
                </div>
                {preview.deals.map(d => (
                  <DealCard key={d.deal_seq} deal={d} registered={registeredSeqs.has(d.deal_seq)} />
                ))}
              </div>
            )}
          </>
        )}

        {/* ---------- Past runs ---------- */}
        <div style={{ marginTop: '20px' }}>
          <CardBase>
            <div style={{ padding: '16px 20px' }}>
              <span style={S.boldText}>過去 run 履歴 ({runs.length}件)</span>
              <table style={{ ...S.table, marginTop: '10px' }}>
                <thead>
                  <tr>
                    <th style={S.th}>run_id</th>
                    <th style={S.th}>対象月</th>
                    <th style={S.th}>closing</th>
                    <th style={S.th}>status</th>
                    <th style={S.th}>社員数</th>
                    <th style={{ ...S.th, ...S.amt }}>総支給</th>
                    <th style={S.th}>balance</th>
                    <th style={S.th}>起票</th>
                    <th style={S.th}>承認</th>
                    <th style={S.th}>登録</th>
                    <th style={S.th}></th>
                  </tr>
                </thead>
                <tbody>
                  {runs.length === 0 && (
                    <tr><td style={S.td} colSpan={11}><Note>run 履歴なし。「D1 保存」で初回 run を作成してください。</Note></td></tr>
                  )}
                  {runs.map(r => (
                    <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => loadExistingRun(r.id)}>
                      <td style={S.td}>{r.id}</td>
                      <td style={S.td}>{r.payment_year}-{String(r.payment_month).padStart(2, '0')}</td>
                      <td style={S.td}>{r.closing_group_id ?? 'both'}</td>
                      <td style={S.td}>
                        <span style={{
                          padding: '2px 8px', borderRadius: '4px', fontSize: '0.6875rem', fontWeight: 700,
                          background:
                            r.status === 'registered' ? '#d3efdc' :
                            r.status === 'approved' ? '#dbe7ff' :
                            r.status === 'draft' ? '#f4f3f1' :
                            r.status === 'failed' ? '#f5d4d4' : '#eee',
                          color:
                            r.status === 'registered' ? '#2da663' :
                            r.status === 'approved' ? '#2e7cf6' :
                            r.status === 'draft' ? '#6b6560' :
                            r.status === 'failed' ? '#c94444' : '#555',
                        }}>{r.status}</span>
                      </td>
                      <td style={S.td}>{r.kot_employee_count}</td>
                      <td style={{ ...S.td, ...S.amt }}>{yen(r.kot_total_amount)}</td>
                      <td style={S.td}>
                        {r.balance_zero ? <StatusIcon type="done">OK</StatusIcon> : <StatusIcon type="error">NG</StatusIcon>}
                      </td>
                      <td style={S.td}>{ymd(r.triggered_at)}</td>
                      <td style={S.td}>{ymd(r.approved_at || undefined) || '—'}</td>
                      <td style={S.td}>{ymd(r.registered_at || undefined) || '—'}</td>
                      <td style={S.td}>
                        <Button onClick={(ev: React.MouseEvent) => { ev.stopPropagation(); loadExistingRun(r.id); }}>開く</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardBase>
        </div>

        {/* Confirm modal: 「v4 完全自動登録」 button click 直後 */}
        <MessageDialogConfirm
          isOpen={v4ButtonState === 'confirming'}
          title={`${paymentYear}年${paymentMonth}月分 完全自動登録`}
          confirmButtonLabel="続行"
          closeButtonLabel="キャンセル"
          onConfirm={onV4AutonomousConfirmed}
          onRequestClose={() => setV4ButtonState('idle')}
          contentAlign="left"
        >
          以下を全部自動で実行します:
          <ol style={{ margin: '6px 0 0 18px' }}>
            <li>D1 保存 (5 deals 生成)</li>
            <li>一括承認</li>
            <li>freee 自動登録 + payments[] で wallet_txn 自動消込 (= section 別)</li>
            <li>通勤費 西武→決済口座 transfer 自動実行</li>
            <li>登録結果検証 → success/error 表示</li>
          </ol>
          <div style={{ marginTop: '8px', fontSize: '0.85rem', color: '#666' }}>
            所要時間: 約 30 秒。 freee UI 上の手動操作は一切不要です。
          </div>
        </MessageDialogConfirm>

        {/* Result modal: 登録完了 (success / error) */}
        <MessageDialog
          isOpen={!!v4ResultModal}
          title={v4ResultModal?.title || ''}
          closeButtonLabel="閉じる"
          onRequestClose={onV4ResultModalClose}
          contentAlign="left"
        >
          {v4ResultModal && (
            <div style={{ minWidth: 480 }}>
              {/* Header: status badge + message */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderRadius: 8, marginBottom: 16,
                background: v4ResultModal.kind === 'success' ? '#e8f5e9' : '#ffebee',
                border: `1px solid ${v4ResultModal.kind === 'success' ? '#a5d6a7' : '#ef9a9a'}`,
              }}>
                {v4ResultModal.kind === 'success'
                  ? <MdCheckCircle size={28} color="#2e7d32" />
                  : <MdError size={28} color="#c62828" />}
                <div style={{ fontSize: '0.95rem', fontWeight: 600, color: v4ResultModal.kind === 'success' ? '#1b5e20' : '#b71c1c' }}>
                  {v4ResultModal.message}
                </div>
              </div>

              {/* Deals list (= 構造化表示) */}
              {v4ResultModal.deals && v4ResultModal.deals.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: '0.78rem', color: '#6b6560', fontWeight: 700, letterSpacing: '0.04em', marginBottom: 6, textTransform: 'uppercase' }}>登録された取引</div>
                  <div style={{ border: '1px solid #e0dcd6', borderRadius: 8, overflow: 'hidden' }}>
                    {v4ResultModal.deals.map((d, i) => {
                      const ok = d.status === 'registered';
                      const labelMap: Record<string, string> = {
                        tri_main: '三井住友 主取引行 振込',
                        tri_grad: '三井住友 新卒 振込',
                        seibu_cash: '西武信金 現金支給',
                        seibu_houshou: '西武信金 予算達成報奨金',
                        decchi_kojo: '決済口座 控除中継',
                        decchi_tsuukin: '決済口座 通勤費 cash',
                        kenpo: '健保+介護',
                        pension: '厚年+子拠',
                        incnet: '控除内訳',
                        jumin: '住民税納付',
                      };
                      return (
                        <div key={i} style={{
                          display: 'grid', gridTemplateColumns: '24px 1fr auto auto',
                          alignItems: 'center', gap: 10, padding: '10px 14px',
                          borderBottom: i < (v4ResultModal.deals!.length - 1) ? '1px solid #efece8' : 'none',
                          background: i % 2 === 0 ? '#fff' : '#faf9f7',
                        }}>
                          {ok ? <MdCheckCircle size={18} color="#2e7d32" /> : <MdError size={18} color="#c62828" />}
                          <div>
                            <div style={{ fontSize: '0.8rem', fontWeight: 600 }}>{labelMap[d.kind] || d.kind}</div>
                            {d.scrub && (
                              <div style={{ fontSize: '0.7rem', color: '#7a7570', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
                                <MdLink size={12} color="#7a7570" /> {d.scrub.startsWith('linked') ? '銀行明細紐付け済' : d.scrub === 'auto_linked_via_payment' ? '決済口座 自動紐付け済' : d.scrub}
                              </div>
                            )}
                            {d.error && <div style={{ fontSize: '0.7rem', color: '#c62828', marginTop: 2 }}>{d.error}</div>}
                          </div>
                          <div style={{ fontSize: '0.85rem', fontFamily: 'monospace', color: '#3a3530', minWidth: 100, textAlign: 'right' }}>
                            ¥{(d.amount || 0).toLocaleString()}
                          </div>
                          {d.deal_id && (
                            <div style={{ fontSize: '0.7rem', color: '#a39d96', fontFamily: 'monospace' }}>
                              #{d.deal_id}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Details (詳細 / stack trace 等、 折りたたみ風) */}
              {v4ResultModal.details && (
                <details>
                  <summary style={{ fontSize: '0.78rem', color: '#6b6560', cursor: 'pointer', padding: '4px 0' }}>詳細を表示</summary>
                  <pre style={{ background: '#f5f3f0', padding: 10, borderRadius: 6, fontSize: '0.7rem', maxHeight: 240, overflow: 'auto', whiteSpace: 'pre-wrap', textAlign: 'left', margin: '4px 0 0 0' }}>
                    {v4ResultModal.details}
                  </pre>
                </details>
              )}
            </div>
          )}
        </MessageDialog>
      </Container>
    </div>
  );
};
